/**
 * RGPD cookie consent banner.
 *
 * Shown as a fixed footer strip on first visit and whenever the visitor
 * clicks the "Cookies" link in the site footer. Non-blocking (does not
 * gate site usage) but analytics/session-replay stays disabled until the
 * user makes a choice.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  CONSENT_KEY,
  denyAnalyticsConsent,
  grantAnalyticsConsent,
  readConsent,
} from "../lib/analytics";

const REOPEN_EVENT = "sn:open-cookie-consent";

export function openCookieConsent() {
  window.dispatchEvent(new CustomEvent(REOPEN_EVENT));
}

export default function CookieConsent() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Show on first visit only. Reopen manually via the footer link.
    if (readConsent().analytics === null) setOpen(true);
    const handler = () => setOpen(true);
    window.addEventListener(REOPEN_EVENT, handler);
    const onStorage = (e) => {
      if (e.key === CONSENT_KEY) setOpen(readConsent().analytics === null);
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(REOPEN_EVENT, handler);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  if (!open) return null;

  const accept = () => {
    grantAnalyticsConsent();
    setOpen(false);
  };
  const refuse = () => {
    denyAnalyticsConsent();
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Bandeau de consentement aux cookies"
      className="fixed bottom-0 left-0 right-0 z-[100] border-t border-primary/30 bg-background/95 backdrop-blur-md p-4 md:p-6"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1 text-sm text-foreground/90 leading-relaxed">
          <p className="font-medium mb-1">🍪 Cookies & mesure d'audience</p>
          <p className="text-muted-foreground">
            Nous utilisons des cookies techniques essentiels au fonctionnement du site.
            Avec votre accord, nous mesurons également l'audience (statistiques anonymisées)
            et enregistrons quelques parcours pour améliorer l'expérience.
            {" "}
            <Link
              to="/mentions-legales"
              className="underline text-primary hover:text-primary/80"
              onClick={() => setOpen(false)}
            >
              En savoir plus
            </Link>
            .
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button
            variant="outline"
            onClick={refuse}
            className="rounded-full"
            data-testid="cookie-refuse"
          >
            Refuser
          </Button>
          <Button
            onClick={accept}
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="cookie-accept"
          >
            Accepter
          </Button>
        </div>
      </div>
    </div>
  );
}
